import { WorkflowChoice, WorkflowFormQuestion } from './types'

/**
 * Builds workflow question objects from raw JSON content.
 *
 * This module parses the workflow definition and transforms each step
 * into a typed WorkflowFormQuestion object with proper input type detection
 * (slider, number, text, date, choice, sketch, pj, calculated).
 *
 * It also extracts validation props (min/max, formats) and handles
 * trigger-based navigation for conditional branching.
 */
export const parseWorkflowContent = (content: any[] | null): any[] => {
    if (!content) return []

    return content
        .map(item => {
            if (typeof item === 'string') {
                try {
                    return JSON.parse(item)
                } catch {
                    return null
                }
            }
            return item
        })
        .filter(item => item && typeof item === 'object')
}

/**
 * Build the raw byId map from the workflow JSON content.
 * Useful for trigger-based navigation lookups in the tunnel page.
 */
export const buildWorkflowByIdMap = (content: any[] | null): Map<string, any> => {
    const parsed = parseWorkflowContent(content)
    const byId = new Map<string, any>()
    for (const step of parsed) {
        if (typeof step?.id === 'string') byId.set(step.id, step)
    }
    return byId
}

export const buildWorkflowFormQuestions = (content: any[] | null): WorkflowFormQuestion[] => {
    const parsed = parseWorkflowContent(content)
    const questions: WorkflowFormQuestion[] = []
    let currentSection = ''
    const byId = new Map<string, any>()

    for (const step of parsed) {
        if (typeof step?.id === 'string') byId.set(step.id, step)
    }
    for (const step of parsed) {
        if (step?.exec?.name === 'section') {
            currentSection = String(step?.exec?.value ?? step?.message ?? '')
            continue
        }
        const isQuestion = typeof step?.id === 'string' && step.id.startsWith('W.Q.')
        const hasMessage = typeof step?.message === 'string'
        if (!isQuestion || !hasMessage) continue

        const section = currentSection || String(step?.sectionLabel ?? '')
        if (!section) continue

        const question = createQuestionFromStep(step, byId, section)
        questions.push(question)
    }
    return questions
}

const createQuestionFromStep = (
    step: any,
    byId: Map<string, any>,
    section: string
): WorkflowFormQuestion => {
    const nextAction = getNextAction(step, byId)
    const { isSliderInput, isNumberInput, isTextInput, isDateInput, isSketchInput, isCalculatedInput } = detectInputTypes(nextAction)
    const choices = extractChoices(nextAction, isTextInput, isNumberInput, isSliderInput)
    const multiple = extractMultiple(nextAction)
    const componentProps = nextAction?.component?.props
    const isAuto = step?.auto === true
    const shouldSaveValue = nextAction?.saveValue === true

    return {
        id: step.id,
        answerId: nextAction?.id,
        sectionLabel: section,
        title: step.message,
        hint: extractHint(step),
        required: !nextAction?.optional,
        optional: Boolean(nextAction?.optional),
        optionalTrigger:
            typeof nextAction?.optional?.trigger === 'string' ? nextAction.optional.trigger : undefined,
        choices,
        multiple,
        inputType: determineInputType(
            isSliderInput,
            isNumberInput,
            isTextInput,
            isDateInput,
            choices,
            isSketchInput,
            Boolean(nextAction?.component?.name === 'TransmissionsPJ'),
            isCalculatedInput
        ),
        auto: isAuto,
        saveValue: shouldSaveValue,
        ...extractNumberProps(isNumberInput, componentProps),
        ...extractSliderProps(isSliderInput, componentProps),
        ...extractDateProps(isDateInput, componentProps),
        ...(isCalculatedInput
            ? {
                  execName: nextAction?.exec?.name ?? '',
                  execValue: nextAction?.exec?.value ?? '',
              }
            : {}),
    }
}

const getNextAction = (step: any, byId: Map<string, any>) => {
    const nextActionId = step?.trigger && typeof step.trigger === 'string' ? step.trigger : undefined
    return nextActionId ? byId.get(nextActionId) : undefined
}

const detectInputTypes = (nextAction: any) => {
    const component = nextAction?.component
    const componentProps = component?.props

    const isCalculatedInput =
        nextAction?.auto === true &&
        nextAction?.exec?.function === true &&
        typeof nextAction?.exec?.value === 'string'

    const isSliderInput = !isCalculatedInput &&
        component &&
        typeof component === 'object' &&
        component?.name === 'TransmissionsSlider' &&
        componentProps &&
        typeof componentProps === 'object' &&
        typeof componentProps?.minimumValue !== 'undefined' &&
        typeof componentProps?.maximumValue !== 'undefined'

    const isNumberInput = !isCalculatedInput &&
        component &&
        typeof component === 'object' &&
        component?.name === 'TransmissionsTextInput' &&
        componentProps &&
        typeof componentProps === 'object' &&
        componentProps?.type === 'number'

    const isDateInput = !isCalculatedInput &&
        component &&
        typeof component === 'object' &&
        component?.name === 'TransmissionsDatePicker' &&
        componentProps &&
        typeof componentProps === 'object' &&
        typeof componentProps?.minimumDate !== 'undefined' &&
        typeof componentProps?.maximumDate !== 'undefined'

    const isSketchInput = !isCalculatedInput &&
        component &&
        typeof component === 'object' &&
        component?.name === 'TransmissionsSketch' &&
        true

    const isTextInput =
        !isCalculatedInput &&
        !isNumberInput &&
        !isSliderInput &&
        !isDateInput &&
        !isSketchInput &&
        ((component && typeof component === 'object' && component?.name === 'TransmissionsTextInput') ||
            (component && typeof component === 'object' && component?.value !== undefined) ||
            (nextAction?.input && typeof nextAction.input === 'object' && nextAction.input?.value !== undefined))
    return { isSliderInput, isNumberInput, isDateInput, isTextInput, isSketchInput, isCalculatedInput }
}

const extractChoices = (
    nextAction: any,
    isTextInput: boolean,
    isNumberInput: boolean,
    isSliderInput: boolean
): WorkflowChoice[] => {
    if (isTextInput || isNumberInput || isSliderInput) return []
    return extractChoicesFromNextAction(nextAction)
}

const extractChoicesFromNextAction = (action: any): WorkflowChoice[] => {
    const options = action?.options
    if (!Array.isArray(options)) return []

    return options
        .map((opt: any, idx: number) => {
            if (opt == null) return null
            if (typeof opt === 'string') {
                return { id: String(idx), label: opt, value: opt }
            }
            if (typeof opt === 'object') {
                const value = String(opt?.value ?? opt?.label ?? opt?.name ?? '')
                const label = String(opt?.label ?? opt?.name ?? opt?.value ?? value)
                if (!value && !label) return null
                return {
                    id: String(opt?.value ?? opt?.label ?? opt?.name ?? idx),
                    label,
                    value,
                    // Extract the trigger from the option if present
                    trigger: typeof opt.trigger === 'string' ? opt.trigger : undefined,
                }
            }
            return null
        })
        .filter(Boolean) as WorkflowChoice[]
}

const extractMultiple = (nextAction: any) => {
    return nextAction?.multiple && typeof nextAction.multiple === 'object'
        ? { trigger: typeof nextAction.multiple.trigger === 'string' ? nextAction.multiple.trigger : undefined }
        : null
}

const extractHint = (step: any): string | undefined => {
    return typeof step?.hint === 'string' ? step.hint : undefined
}

const determineInputType = (
    isSliderInput: boolean,
    isNumberInput: boolean,
    isTextInput: boolean,
    isDateInput: boolean,
    choices: WorkflowChoice[],
    isSketchInput: boolean,
    isPJInput: boolean = false,
    isCalculatedInput: boolean = false
): 'slider' | 'number' | 'text' | 'date' | 'choice' | 'sketch' | 'pj' | 'calculated' | 'message' | undefined => {
    if (isCalculatedInput) return 'calculated'
    if (isPJInput) return 'pj'
    if (isSketchInput) return 'sketch'
    if (isSliderInput) return 'slider'
    if (isNumberInput) return 'number'
    if (isTextInput) return 'text'
    if (isDateInput) return 'date'
    if (choices.length > 0) return 'choice'
    return 'message'
}

const extractNumberProps = (isNumberInput: boolean, componentProps: any) => {
    if (!isNumberInput) {
        return {
            numberMin: undefined,
            numberMax: undefined,
            numberExpectedFormat: undefined,
        }
    }
    return {
        numberMin: componentProps?.min,
        numberMax: componentProps?.max,
        numberExpectedFormat:
            typeof componentProps?.expectedFormat === 'string'
                ? componentProps.expectedFormat
                : 'integer',
    }
}

const extractSliderProps = (isSliderInput: boolean, componentProps: any) => {
    if (!isSliderInput) {
        return {
            sliderMin: undefined,
            sliderMax: undefined,
            sliderStep: undefined,
        }
    }
    return {
        sliderMin: componentProps?.minimumValue,
        sliderMax: componentProps?.maximumValue,
        sliderStep: componentProps?.step,
    }
}

const extractDateProps = (isDateInput: boolean, componentProps: any) => {
    if (!isDateInput) {
        return {
            dateMin: undefined as any,
            dateMax: undefined as any,
        }
    }
    return {
        dateMin: componentProps?.minimumDate,
        dateMax: componentProps?.maximumDate,
    }
}
